/**
 * ImageSkeleton web component
 *
 * Hides the `.image-skeleton` overlay once the contained <img> has loaded.
 * Uses a MutationObserver so it also handles cases where Dawn replaces the
 * <img> element entirely (e.g. product media gallery variant switching).
 *
 * Usage:
 *   <image-skeleton>
 *     <div style="position: relative;">
 *       <div class="image-skeleton"></div>
 *       <img src="..." loading="lazy" ...>
 *     </div>
 *   </image-skeleton>
 */
class ImageSkeleton extends HTMLElement {
  connectedCallback() {
    this._skeleton = this.querySelector('.image-skeleton');
    if (!this._skeleton) return;

    this._bindToImg(this.querySelector('img'));

    // Watch for Dawn replacing the <img> node (variant/gallery changes)
    this._observer = new MutationObserver(() => {
      const img = this.querySelector('img');
      if (img && img !== this._currentImg) {
        this._bindToImg(img);
      }
    });
    this._observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }

  _bindToImg(img) {
    this._currentImg = img;

    if (!img) {
      this._hide();
      return;
    }

    // Already loaded (cached)
    if (img.complete && img.naturalWidth > 0) {
      this._hide();
      return;
    }

    // Show skeleton while this image loads
    this._show();
    img.addEventListener('load', () => this._hide(), { once: true });
    img.addEventListener('error', () => this._hide(), { once: true });
  }

  _show() {
    this._skeleton?.classList.remove('image-skeleton--hidden');
  }

  _hide() {
    this._skeleton?.classList.add('image-skeleton--hidden');
  }
}

customElements.define('image-skeleton', ImageSkeleton);
